/**
 * @module AuroraRender
 * @description Singleton module that manages Aurora UI/UX/State Renders.
 */
const AuroraRender = (function () {
  /**
   * An object representing the module's settings.
   *
   * @private
   * @type {object} settings
   * @description The initial settings for the singleton object.
   */
  const settings = {
    name: "aurora-render.js",
    debugging: true,
    isSiteFrench: AuroraUtils.isSiteFrench(),
  };

  /**
   * Enable logging for the module.
   *
   * @type {boolean}
   */
  const LOGGING = true;

  /**
   * An object representing the module's target elements.
   *
   * @private
   * @type {object} elements
   * @description Cached collection of render-specific HTML elements.
   */
  let elements = getElements();

  let timeoutTimer = false;

  const addToCartFail = (message = "Oops! Add to cart failed :(") => {
    if (elements.notification.cartUpdate) {
      elements.notification.cartUpdate.innerHTML = message;
      elements.notification.cartUpdate.classList.add("show");

      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
      }

      timeoutTimer = setTimeout(() => {
        elements.notification.cartUpdate.classList.remove("show");
      }, 1500);
    } else {
      alert(message);
    }
  };

  const addToCartSuccess = (responseData = {}, items = []) => {
    let totalItemsAdded = 0;
    const responseDataItems = responseData?.items || [];
    const addedProducts = items.map((item, index) => {
      return { ...responseDataItems[index], ...item };
    });

    // calculate total items added to cart
    addedProducts.forEach((item) => (totalItemsAdded += item.quantity));

    // conditionally build message based on addedProducts array length
    switch (addedProducts.length) {
      case 0: {
        message = isSiteFrench
          ? `Oups! On dirait que quelque chose s'est mal passé. Rien n'a été ajouté à votre panier.`
          : `Oops! It looks like something went wrong. Nothing was added to cart.`;
        break;
      }
      case 1: {
        const isCannabisProduct = AuroraProduct.isCannabisProduct(addedProducts[0]);
        const productOptions = addedProducts[0].variant_options[0];
        if (isCannabisProduct) {
          message = settings.isSiteFrench
            ? `<p><strong>${addedProducts[0].properties._fr_product_title} – ${productOptions}</strong> a été ajouté au panier!</p>`
            : `<p><strong>${addedProducts[0].product_title} – ${productOptions}</strong> added to cart!</p>`;
        } else {
          message = settings.isSiteFrench
            ? `<p><strong>${addedProducts[0].properties._fr_product_title}</strong> a été ajouté au panier!</p>`
            : `<p><strong>${addedProducts[0].title}</strong> added to cart!</p>`;
        }
        break;
      }
      default: {
        message = settings.isSiteFrench
          ? `<p><strong>${totalItemsAdded} articles</strong> ont été ajoutés à votre panier!</p>`
          : `<p><strong>${totalItemsAdded} items</strong> added to cart!</p>`;
        break;
      }
    }

    // render message to visitor
    if (elements.notification.cartUpdate) {
      elements.notification.cartUpdate.innerHTML = message;
      elements.notification.cartUpdate.classList.add("show");

      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
      }

      timeoutTimer = setTimeout(() => {
        elements.notification.cartUpdate.classList.remove("show");
      }, 1500);
    } else {
      alert(message);
    }
  };

  const cartLineItems = async (cart = {}, cartAdditionalData = {}) => {
    const { items: cartItems } = cart;

    const lineItemsNodes = Array.from(elements.cart.lineItems);

    await Promise.all(
      lineItemsNodes.map(async (element) => {
        const templates = await Promise.all(cartItems.map((item) => AuroraTemplates.cartLineItem(item, cartAdditionalData)));
        element.innerHTML = templates.join("");
      })
    );
  };

  const cartStates = (cart = {}) => {
    console.log("cartStates", { cart });

    const { item_count, items_subtotal_price, original_total_price, total_discount } = cart;

    cartMessageEmptyCart(item_count);
    cartMessageFreeShipping(items_subtotal_price);
    cartItemCountTotal(item_count);
    cartItemSummary(item_count);

    cartSubTotal(items_subtotal_price);
    cartTotal(original_total_price);
    cartTotalDiscounts(total_discount);
  };

  const cartMessageEmptyCart = function (item_count = 0) {
    elements.cart.messageEmptyCart.forEach((el) => {
      el.classList[item_count > 0 ? "add" : "remove"]("hide");
    });
  };

  const cartMessageFreeShipping = function (items_subtotal_price = 0) {
    let message = "";
    let subtotal = items_subtotal_price / 100;
    let remaining = AuroraUtils.formatMoney(100 - subtotal);

    console.log("cartMessageFreeShipping || settings", settings);

    if (subtotal < 100) {
      // caculate how much before free shipping
      message = settings.isSiteFrench
        ? `Dépensez encore ${remaining} CAN pour obtenir la livraison <b>GRATUITE</b>`
        : `Spend ${remaining} CAD more for <b>FREE</b> shipping`;
    } else {
      // free shipping
      message = settings.isSiteFrench
        ? `Cette commande est admissible à la livraison <b>GRATUITE</b>`
        : `This order qualifies for <b>FREE</b> shipping`;
    }

    elements.cart.messageFreeShipping.forEach((el) => (el.innerHTML = message));
  };

  /**
   * Render cart attributes to a given element.
   *
   * @function cartAttributesFromCart
   * @param {Object} cart - The cart object.
   * @param {HTMLElement} element - The element to render the cart attributes to.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  function cartAttributesFromCart(cart = {}, element = false) {
    // log cart object
    LOGGING && console.log("[ cartAttributesFromCart() ] :: init :: cart and element...", { cart, element });

    if (element) {
      // initialize template
      let template = "";
      const attributes = cart?.attributes ?? {};

      // add token to template
      template += `<div class="debugger-grid__row">Token: <b>${cart.token}</b></div>`;

      // add attributes to template
      for (const property in attributes) {
        const attribute = attributes[property] || "";
        if ("address" === property) {
          const address = JSON.parse(attribute);
          for (const addressProperty in address) {
            template += `<div class="debugger-grid__row">Address, ${addressProperty}: <b>${address[addressProperty]}</b></div>`;
          }
        } else {
          template += `<div class="debugger-grid__row">${property}: <b>${attribute}</b></div>`;
        }
      }

      // render template to element
      element.innerHTML = template;
    }
  }

  const toggleFreeShippingMessageVisibility = function (total_price = 0) {
    const showMessage = total_price / 100 > 100 ? true : false;
    elements.cart.messageFreeShipping.forEach((el) => {
      el.classList[showMessage ? "remove" : "add"]("hide");
    });
  };

  const cartItemCountTotal = (items = 0) => {
    elements.cart.itemCountTotal.forEach((el) => {
      el.textContent = `${items}`;
    });
  };

  const cartItemSummary = (items = 0) => {
    elements.cart.summary.forEach((el) => {
      el.textContent = settings.isSiteFrench ? `${items} article${items > 1 ? "s" : ""}` : `${items} item${items > 1 ? "s" : ""}`;
    });
  };

  const cartLineItemPriceByKey = (lineKey = "", cart = {}) => {
    const filteredItem = cart.items.filter((item) => item.key === lineKey);
    const lineItemElement = document.getElementById(`cart-line-item-price--${lineKey}`);

    if (filteredItem.length && lineItemElement) {
      lineItemElement.textContent = AuroraUtils.formatMoney(filteredItem[0].original_line_price / 100);
    }
  };

  const cartLineItemRemovalByKey = (key = "") => {
    document.getElementById(`cart-line-item--${key}`).remove();
  };

  const cartLineItemRemoval = (element = false) => {
    element.remove();
  };

  const cartSubTotal = (subtotal = 0) => {
    elements.cart.subtotal.forEach((el) => {
      el.textContent = AuroraUtils.formatMoney(subtotal / 100);
    });
  };

  const cartTotal = (total = 0) => {
    elements.cart.total.forEach((el) => {
      el.textContent = AuroraUtils.formatMoney(total / 100);
    });
  };

  const cartTotalDiscounts = (totalDiscounts = 0) => {
    const formattedDiscounts = AuroraUtils.formatMoney(totalDiscounts / 100);
    elements.cart.totalDiscounts.forEach((el) => {
      el.textContent = totalDiscounts === 0 ? formattedDiscounts : `-${formattedDiscounts}`;
    });
  };

  const customerStates = (customer = {}, cart = {}, cartAdditionalData = {}, isAddToCart = false) => {
    if (settings.debugging) console.log(`${settings.name} :: customerStates() :: customer object`, customer);

    const cartItemsWithOrderLimits = AuroraCart.getCartItemsWithOrderLimits(cart.items, cartAdditionalData);
    const cartWithinOrderLimit = AuroraCart.itemsWithinOrderLimit(cartItemsWithOrderLimits, cartAdditionalData);
    const allowanceRemaining = AuroraCustomer.getAllowanceRemaining(customer);
    const prescriptionOrderLimit = AuroraCustomer.getPrescriptionOrderLimit(customer);
    const totalGramsFromCartItems = AuroraCart.getTotalGramsFromCartItems(cart.items, cartAdditionalData);
    const gramLimitPercentage = AuroraCustomer.getGramLimitPercentage(prescriptionOrderLimit, totalGramsFromCartItems);
    const withinGramLimit = totalGramsFromCartItems <= prescriptionOrderLimit;

    // limited items states
    renderCartBeverageLimitWarning(cartItemsWithOrderLimits);

    // prescription states
    prescriptionAllowanceRemaining(allowanceRemaining);
    prescriptionLimitMessage(prescriptionOrderLimit, totalGramsFromCartItems);
    prescriptionLimitMeter(gramLimitPercentage);
    prescriptionOrderLimitSummary(prescriptionOrderLimit, totalGramsFromCartItems, cart, cartAdditionalData);

    // toggle limited cart items warning
    if (cartWithinOrderLimit) {
      toggleLimitedCartItemsWarning("disabled");
    } else {
      toggleLimitedCartItemsWarning("enabled");
    }

    // toggle prescription limit warning
    if (withinGramLimit) {
      togglePrescriptionLimitWarning("disabled");
    } else {
      togglePrescriptionLimitWarning("enabled");
    }

    if (isAddToCart) {
      showPrescriptionBanner({
        showdelay: 1500,
        autohide: 3000,
        hideElements: [".header-prescription-column", ".header-vac-column"],
      });
    }
  };

  function getElements() {
    return {
      cart: {
        buttonCheckout: document.querySelector("button[name='checkout']"),
        mainCartCheckout: document.querySelector(".aur-main-cart-checkout"),
        drawer: document.querySelector(".js--cart-drawer"),
        warnings: {
          beverageLimit: document.querySelectorAll(".js--cart-warning-beverage-limit") || [],
        },
        lineItems: document.querySelectorAll(".js--cart-line-items") || [],
        messageEmptyCart: document.querySelectorAll(".js--cart-empty-message") || [],
        messageFreeShipping: document.querySelectorAll(".js--cart-free-shipping-message") || [],
        itemCountTotal: document.querySelectorAll(".js--cart-item-count-total") || [],
        subtotal: document.querySelectorAll(".js--cart-subtotal") || [],
        total: document.querySelectorAll(".js--cart-total") || [],
        totalDiscounts: document.querySelectorAll(".js--cart-total-discounts") || [],
        summary: document.querySelectorAll(".js--cart-item-summary") || [],
      },
      notification: {
        cartUpdate: document.getElementById("notification-cart-update"),
      },
      prescription: {
        allowanceRemaining: document.querySelectorAll(".js--prescription-allowance-remaining-total") || [],
        limitMessage: document.querySelectorAll(".js--prescription-limit-message") || [],
        limitMeter: document.querySelectorAll(".js--prescription-order-limit-meter") || [],
        limitMeterWarning: document.querySelectorAll(".js--prescription-order-limit-warning") || [],
        limitSummary: document.querySelectorAll(".js--prescription-order-limit-summary") || [],
        totalCoverage: document.querySelectorAll(".js--insurance_remaining_coverage") || [],
        orderLimit: document.querySelectorAll(".js--prescription-order-limit") || [],
      },
    };
  }

  function renderCartBeverageLimitWarning(cartItemsWithOrderLimits = []) {
    const tagLimitedItems = cartItemsWithOrderLimits.filter((lineItem) => AuroraCart.hasTagLimit(lineItem));

    if (tagLimitedItems.length > 0) {
      const beverageLocales = window.theme?.locales?.restrictions?.cart?.orderLimits?.beverages || {};
      const limitedItemsQtyMax = 12;
      let limitedItemsQtyCurrent = 0;

      tagLimitedItems.forEach((lineItem) => (limitedItemsQtyCurrent += lineItem.quantity));

      const difference = limitedItemsQtyCurrent - limitedItemsQtyMax;
      let template = "";
      let stateClass = "";

      if (difference > 0) {
        template = beverageLocales.overLimit || "";
        stateClass = "over-limit";
      } else if (difference === 0) {
        template = beverageLocales.atLimit || "";
        stateClass = "at-limit";
      } else {
        template = beverageLocales.underLimit || "";
        stateClass = "under-limit";
      }

      const message = template
        .replace(/\[\[CURRENT\]\]/g, limitedItemsQtyCurrent)
        .replace(/\[\[LIMIT\]\]/g, limitedItemsQtyMax)
        .replace(/\[\[DIFFERENCE\]\]/g, difference);

      elements.cart.warnings.beverageLimit.forEach((el) => {
        el.classList.remove("hide", "under-limit", "at-limit", "over-limit");
        el.classList.add(stateClass);
        el.innerHTML = message;
      });
    } else {
      elements.cart.warnings.beverageLimit.forEach((el) => {
        el.classList.remove("under-limit", "at-limit", "over-limit");
        el.classList.add("hide");
      });
    }
  }

  const prescriptionAllowanceRemaining = (remaining = 0) => {
    elements.prescription.allowanceRemaining.forEach((el) => {
      el.innerHTML = settings.isSiteFrench
        ? `Il vous reste <b>${remaining}</b> gramme` + (remaining > 1 ? "s" : "") + ` dans votre ordonnance actuelle.`
        : `You have <b>${remaining}</b> gram` + (remaining > 1 ? "s" : "") + ` left in your current prescription.`;
    });
  };

  const prescriptionLimitMeter = (percent = 0) => {
    elements.prescription.limitMeter.forEach((el) => (el.style.width = `${percent}%`));
  };

  const updateRemainingCoverageView = (cart, cartAdditionalData) => {
    const remainingCoverage = parseFloat(AuroraCustomer.info.insurance_remaining_coverage);

    if (remainingCoverage > 0) {
      const totalGramsFromCartItems = AuroraCart.getTotalGramsFromCartItems(cart.items, cartAdditionalData);
      const coveredGrams = Math.min(totalGramsFromCartItems, remainingCoverage);
      const difference = (remainingCoverage - coveredGrams).toFixed(2);

      LOGGING &&
        console.log("[ updateRemainingCoverageView() ] ::", {
          totalGramsFromCartItems,
          coveredGrams,
          difference,
        });

      // Update remaining grams messaging
      (document.querySelectorAll(".js--insurance_remaining_coverage") || []).forEach((element) => {
        element.innerHTML = settings.isSiteFrench ? `${difference} gramme(s) restant(s).` : `${difference} gram(s) remaining.`;
      });

      // Toggle the coverage alert visibility based on remaining coverage
      (document.querySelectorAll(".coverage_alert, .js--insurance-coverage-exceeded") || []).forEach((el) => {
        const showMesssage = totalGramsFromCartItems > coveredGrams;
        el.classList[showMesssage ? "remove" : "add"]("hide");
      });
    }
  };

  const prescriptionOrderLimitSummary = (orderLimit = 0, currentUsage = 0, cart, cartAdditionalData = {}) => {
    // Display the current grams used and the order limit
    const currentUsageText = settings.isSiteFrench
      ? `${currentUsage} gramme` + (currentUsage > 1 ? "s" : "")
      : `${currentUsage} gram` + (currentUsage > 1 ? "s" : "");
    const orderLimitText = settings.isSiteFrench ? `Limite de ${orderLimit} gramme(s) par commande` : `${orderLimit} gram(s) order limit`;
    // Update the UI elements to show current usage and the order limit
    elements.prescription.limitSummary.forEach((el) => {
      el.textContent = `${currentUsageText} / ${orderLimitText}`;
    });
    if (cart) {
      updateRemainingCoverageView(cart, cartAdditionalData);
    }
  };

  const prescriptionLimitMessage = (remaining = 0) => {
    elements.prescription.limitMessage.forEach((el) => {
      el.innerHTML = settings.isSiteFrench
        ? `Il vous reste <span>${remaining} gramme` + (remaining > 1 ? "s" : "") + `</span> dans votre ordonnance actuelle`
        : `You have <span>${remaining} gram` + (remaining > 1 ? "s" : "") + `</span> left in your current prescription`;
    });
  };

  const productDCE = (elements = [], dceValue = "") => {
    dceValue && elements.forEach((el) => (el.innerHTML = dceValue));
  };

  const productDceFromButton = (button = {}) => {
    const elements = button.form.querySelectorAll(".js--product-dce-value") || [];
    const dceValue = button.getAttribute("data-variant-dce-value") || "";

    elements.forEach((el) => (el.innerHTML = dceValue));
  };

  /**
   * Reads pricing and pack size data attributes from a variant button, builds
   * a localised price template, and updates all matching `.js--product-pricing`
   * elements within the button's form.
   *
   * @param {HTMLElement} button - The variant option button element.
   *
   * @description Prices are stored as integers (cents) on data attributes and
   * divided by 100 before formatting. Pack size UOM is localised for French when
   * settings.isSiteFrench is true. Reads data-variant-price, data-variant-price-compare-at,
   * data-variant-pack-size, and data-variant-pack-size-uom from the button element.
   *
   * @author Brenden Mersey
   */
  const productPriceFromButton = (button = {}) => {
    let packSizeUOM = button.getAttribute("data-variant-pack-size-uom") || 0;

    const elements = button.form.querySelectorAll(".js--product-pricing") || [];
    const packSize = button.getAttribute("data-variant-pack-size") || 0;
    const price = parseInt(button.getAttribute("data-variant-price"));
    const priceCompare = parseInt(button.getAttribute("data-variant-price-compare-at"));
    const formattedPrice = AuroraUtils.formatMoney(price / 100);
    const formattedPriceCompare = AuroraUtils.formatMoney(priceCompare / 100);

    if (settings.isSiteFrench) {
      packSizeUOM = packSizeUOM.replace("ea", "ch.");
    }

    let template = `<span class="product__price">${formattedPrice}</span>`;

    if (priceCompare > price) {
      template += `<span class="product__price-compare">${formattedPriceCompare}</span>`;
    }

    if (packSize && packSizeUOM) {
      template += `<span class="product__pack-size">${packSize} ${packSizeUOM}</span>`;
    }

    elements.forEach((el) => {
      el.classList.remove("on-sale");
      if (priceCompare > price) el.classList.add("on-sale");
      el.innerHTML = template;
    });
  };

  const setButtonState = function (button = {}, state = "enabled") {
    button.disabled = state === "disabled" ? true : false;
  };

  /**
   * Toggle the limited cart items warning to elements.
   *
   * @param {string} state - The state to toggle the warning to.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  const toggleLimitedCartItemsWarning = function (state = "enabled") {
    // Toggle the limited cart items warning to the meter.
    elements.prescription.limitMeter.forEach((el) => {
      el.classList[state === "enabled" ? "add" : "remove"]("over-limit");
    });

    // Toggle the limited cart items warning to the summary.
    elements.cart.warnings.beverageLimit.forEach((el) => {
      el.classList[state === "enabled" ? "add" : "remove"]("over-limit");
    });
  };

  /**
   * Toggle the prescription limit warnings.
   *
   * @param {string} state - The state to toggle the warning to.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  const togglePrescriptionLimitWarning = function (state = "enabled") {
    // Toggle the prescription limit warning to the meter.
    elements.prescription.limitMeter.forEach((el) => {
      el.classList[state === "enabled" ? "add" : "remove"]("over-limit");
    });

    // Toggle the prescription limit warning to the summary.
    elements.prescription.orderLimit.forEach((el) => {
      el.classList[state === "enabled" ? "remove" : "add"]("hide");
    });
  };

  const updateCheckoutButtonState = function (customer = {}, cart = {}, cartAdditionalData = {}) {
    if (elements.cart.buttonCheckout) {
      const cartItemsWithOrderLimits = AuroraCart.getCartItemsWithOrderLimits(cart.items, cartAdditionalData);
      const pescriptionOrderLimit = AuroraCustomer.getPrescriptionOrderLimit(customer);
      const totalGramsFromCartItems = AuroraCart.getTotalGramsFromCartItems(cart.items, cartAdditionalData);

      const isCustomerWithinGramLimit = totalGramsFromCartItems <= pescriptionOrderLimit;
      const isCustomerWithinProductItemsOrderLimit = AuroraCart.itemsWithinOrderLimit(cartItemsWithOrderLimits, cartAdditionalData);
      const customerAcceptedTermsConditions = AuroraCart.inputTermsConditionsChecked();

      const enableCheckout = isCustomerWithinGramLimit && isCustomerWithinProductItemsOrderLimit && customerAcceptedTermsConditions ? true : false;

      elements.cart.buttonCheckout.disabled = enableCheckout ? false : true;
    }
  };

  const refresh = function () {
    elements = getElements();
  };

  return {
    addToCartFail,
    addToCartSuccess,
    cartAttributesFromCart,
    cartItemCountTotal,
    cartItemSummary,
    cartLineItemPriceByKey,
    cartLineItemRemoval,
    cartLineItemRemovalByKey,
    cartLineItems,
    cartMessageEmptyCart,
    cartMessageFreeShipping,
    cartStates,
    cartSubTotal,
    customerStates,
    prescriptionLimitMeter,
    prescriptionOrderLimitSummary,
    productDCE,
    productDceFromButton,
    productPriceFromButton,
    refresh,
    setButtonState,
    toggleFreeShippingMessageVisibility,
    togglePrescriptionLimitWarning,
    updateCheckoutButtonState,
  };
})();
